import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { createTextTransform } from "../../../actions/text-transform/createTextTransform";
import ExtractChatPanel from "../../../components/chat/ExtractChatPanel";
import Dropzone from "../../../components/forms/GenericDropzone";
import UploadedFilesList from "../../../components/lists/UploadedFilesList";
import PdfPasswordModal from "../../../components/modals/PdfPasswordModal";
import InlineProcessingStatus from "../../../components/processing/InlineProcessingStatus";
import { useUploadForm } from "../../../hooks/useUploadForm";
import { scribePrimaryCtaContainerSx } from "../../../constants/scribeUi";
import GlassPanel from "../GlassPanel";

interface ExtractChatTabProps {
  isApproved: boolean;
  injectedText?: string;
  onInjectedTextConsumed?: () => void;
}

const ExtractChatTab: React.FC<ExtractChatTabProps> = ({
  isApproved,
  injectedText,
  onInjectedTextConsumed,
}) => {
  const uploadForm = useUploadForm({ minFiles: 1, maxFiles: 1, maxTotalSizeMB: 60 });
  const [extractedText, setExtractedText] = useState("");
  const [chatJobId, setChatJobId] = useState("");
  const [chatFileName, setChatFileName] = useState("");

  useEffect(() => {
    if (injectedText?.trim()) {
      setExtractedText(injectedText);
      setChatJobId(`injected-${Date.now()}`);
      setChatFileName("Q&A Assist Response");
      onInjectedTextConsumed?.();
    }
  }, [injectedText, onInjectedTextConsumed]);

  const uploadHandler = uploadForm.createApiUploadHandler(
    async (fileKeys, filePasswords) =>
      createTextTransform(fileKeys, {
        sourceText: "",
        transformType: "improve",
        customInstruction: "Extract and return the full text content of this document exactly as-is. Do not summarize, rephrase, or alter the text in any way.",
        additionalContext: "",
        filePasswords,
      }),
    () => {},
  );

  const handleSubmit = (e: React.FormEvent) => uploadForm.handleSubmit(e);
  const showChat = !!chatJobId && !!extractedText;

  const handleTextExtracted = useCallback((text: string) => {
    const currentFileName = uploadForm.files[0]?.name || "Uploaded document";
    setExtractedText(text);
    setChatJobId(uploadForm.jobId);
    setChatFileName(currentFileName);
    uploadForm.setProcessing(false);
    uploadForm.setShowLoader(false);
  }, [uploadForm]);

  const handleReset = useCallback(() => {
    setExtractedText("");
    setChatJobId("");
    setChatFileName("");
    uploadForm.clearFiles();
    uploadForm.setJobId("");
    uploadForm.setShowLoader(false);
    uploadForm.setProcessing(false);
  }, [uploadForm]);

  const handleLoaderClose = useCallback(() => {
    uploadForm.handleModalClose();
    setExtractedText("");
    setChatJobId("");
    setChatFileName("");
  }, [uploadForm]);

  return (
    <>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--teal)",
          mb: 1,
        }}
      >
        Doc Chat
      </Typography>

      <GlassPanel>
        {!showChat && (
          <Box sx={{ opacity: uploadForm.processing ? 0.65 : 1, pointerEvents: uploadForm.processing ? "none" : "auto" }}>
            <form onSubmit={handleSubmit}>
              <Dropzone
                onDrop={uploadForm.createDropHandler(isApproved)}
                multiple={false}
                mainText="Upload Document"
                subText="PDF or DOCX — extract text first, then chat about the content"
                buttonText="Select Document (.pdf, .docx)"
              />
              <UploadedFilesList
                title="Selected Document"
                files={uploadForm.files}
                onRemoveFile={uploadForm.removeFile}
                filePasswords={uploadForm.filePasswords}
              />
              {uploadForm.fileError && (
                <Typography color="error" sx={{ mt: 1, fontSize: "0.82rem" }}>
                  {uploadForm.fileError}
                </Typography>
              )}

              <Box sx={[{ mt: 3 }, scribePrimaryCtaContainerSx]}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={!isApproved || uploadForm.files.length !== 1 || uploadForm.processing}
                >
                  Start Chat
                </Button>
              </Box>
            </form>
          </Box>
        )}

        <InlineProcessingStatus
          open={uploadForm.showLoader}
          files={uploadForm.files}
          jobId={uploadForm.jobId}
          userId={uploadForm.userId}
          onUpload={uploadHandler}
          // #611 — clearing `processing` only in handleTextExtracted is not
          // enough: that fires from the loader's preview effect, which is
          // skipped when the finished job has no output text (empty result, or
          // output metadata without a contentTextKey). In that state the tab
          // stayed frozen — every control here is gated on `processing`.
          // Completion is the signal that must always clear it.
          onComplete={async () => uploadForm.setProcessing(false)}
          onTextExtracted={handleTextExtracted}
          onClose={handleLoaderClose}
        />

        {showChat && (
          <ExtractChatPanel
            jobId={chatJobId}
            fileName={chatFileName}
            extractedText={extractedText}
            onStartOver={handleReset}
          />
        )}
      </GlassPanel>
      <PdfPasswordModal
        open={uploadForm.showPasswordModal}
        file={uploadForm.currentFileNeedingPassword}
        onSubmit={uploadForm.handlePasswordSubmit}
        onSkip={uploadForm.handlePasswordSkip}
        onCancel={uploadForm.handlePasswordCancel}
      />
    </>
  );
};

export default ExtractChatTab;
